import { Download } from 'lucide-react';
import { useCallback, useState } from 'react';
import PageGuide from '../../components/analytics/PageGuide';
import { analyticsService } from '../../services/api';
import '../Analytics.css';
import AnalyticsLayout from './AnalyticsLayout';

function getDefaultFromDate() {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
}

const formatCurrency = (value) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);

const formatNumber = (value) =>
    new Intl.NumberFormat('it-IT').format(value || 0);

function buildCsvContent(sections) {
    const rows = [];
    for (const section of sections) {
        rows.push([section.title]);
        rows.push(['Metrica', 'Valore']);
        for (const row of section.rows) {
            rows.push([row.label, row.value]);
        }
        rows.push(['']);
    }
    return rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
}

function downloadCsv(content, filename) {
    const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export default function AnalyticsExport() {
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);
    const [filters, setFilters] = useState({
        from: getDefaultFromDate(),
        to: new Date().toISOString().split('T')[0]
    });

    const handleExport = useCallback(async () => {
        try {
            setLoading(true);
            setStatus(null);

            const [overview, traction, revenue, activation, operations] = await Promise.all([
                analyticsService.getOverview({ from: filters.from, to: filters.to, groupBy: 'day' }),
                analyticsService.getTraction({ from: filters.from, to: filters.to }),
                analyticsService.getRevenue({ from: filters.from, to: filters.to }),
                analyticsService.getActivation({ from: filters.from, to: filters.to }),
                analyticsService.getOperations({ from: filters.from, to: filters.to })
            ]);

            const sections = [
                {
                    title: 'OVERVIEW',
                    rows: [
                        { label: 'Periodo Da', value: overview.period?.from },
                        { label: 'Periodo A', value: overview.period?.to },
                        { label: 'Tenant Totali', value: formatNumber(overview.tenants?.total) },
                        { label: 'Tenant Attivi', value: formatNumber(overview.tenants?.active) },
                        { label: 'Locali Totali', value: formatNumber(overview.venues?.total) },
                        { label: 'Locali Attivi (con transazioni)', value: formatNumber(overview.venues?.active) },
                        { label: 'Device Totali', value: formatNumber(overview.devices?.total) },
                        { label: 'Device Attivi', value: formatNumber(overview.devices?.active) },
                        { label: 'Totale Mance', value: formatCurrency(overview.tips?.totalAmount) },
                        { label: 'Numero Transazioni', value: formatNumber(overview.tips?.transactionCount) },
                        { label: 'Mancia Media', value: formatCurrency(overview.tips?.averageAmount) },
                        { label: 'Success Rate', value: `${overview.tips?.successRate}%` },
                        { label: 'Failure Rate', value: `${overview.tips?.failureRate}%` },
                        { label: 'Inviti Percettori Totali', value: formatNumber(overview.onboarding?.invited) },
                        { label: 'Percettori Accettati', value: formatNumber(overview.onboarding?.completed) },
                        { label: 'Percettori Pending', value: formatNumber(overview.onboarding?.pending) }
                    ]
                },
                {
                    title: 'TRACTION',
                    rows: [
                        { label: 'Volume Mance (periodo)', value: formatCurrency(traction.summary?.totalVolume) },
                        { label: 'Transazioni (periodo)', value: formatNumber(traction.summary?.totalTransactions) },
                        { label: 'Locali Attivi (periodo)', value: formatNumber(traction.summary?.activeVenuesCount) },
                        { label: 'Locali Inattivi (periodo)', value: formatNumber(traction.summary?.inactiveVenuesCount) }
                    ]
                },
                {
                    title: 'REVENUE',
                    rows: [
                        { label: 'Gross Tip Volume (GTV)', value: formatCurrency(revenue.grossVolume) },
                        { label: 'Transazioni', value: formatNumber(revenue.transactionCount) },
                        { label: 'Fee Stimata Soontip', value: formatCurrency(revenue.estimatedSoontipFee) },
                        { label: 'Copertura Dati Fee (%)', value: `${revenue.feeDataCoverage}%` },
                        { label: 'Revenue Confermata (Fatture)', value: formatCurrency(revenue.confirmedRevenue?.amount) },
                        { label: 'Fatture Emesse', value: formatNumber(revenue.confirmedRevenue?.invoiceCount) },
                        { label: 'Revenue Pending', value: formatCurrency(revenue.pendingRevenue) },
                        { label: 'Run-rate Mensile GTV', value: formatCurrency(revenue.monthlyRunRate?.grossVolume) },
                        { label: 'Run-rate Mensile Fee', value: formatCurrency(revenue.monthlyRunRate?.estimatedFee) }
                    ]
                },
                {
                    title: 'ACTIVATION',
                    rows: [
                        { label: 'Tenant Totali', value: formatNumber(activation.tenants?.total) },
                        { label: 'Tenant Attivi', value: formatNumber(activation.tenants?.active) },
                        { label: 'Tenant Sospesi', value: formatNumber(activation.tenants?.suspended) },
                        { label: 'Tenant Nuovi (periodo)', value: formatNumber(activation.tenants?.newInPeriod) },
                        { label: 'Tenant Activation Rate', value: `${activation.tenants?.activationRate}%` },
                        { label: 'Device Totali', value: formatNumber(activation.devices?.total) },
                        { label: 'Device Attivi', value: formatNumber(activation.devices?.active) },
                        { label: 'Device Activation Rate', value: `${activation.devices?.activationRate}%` },
                        { label: 'Inviti Worker (periodo)', value: formatNumber(activation.workerInvites?.inPeriod?.invited) },
                        { label: 'Inviti Accettati (periodo)', value: formatNumber(activation.workerInvites?.inPeriod?.accepted) },
                        { label: 'Acceptance Rate (periodo)', value: `${activation.workerInvites?.inPeriod?.acceptanceRate}%` },
                        { label: 'Inviti All-time', value: formatNumber(activation.workerInvites?.allTime?.invited) },
                        { label: 'Acceptance Rate All-time', value: `${activation.workerInvites?.allTime?.acceptanceRate}%` },
                        { label: 'Stripe Connect Avviati', value: formatNumber(activation.stripeConnect?.started) },
                        { label: 'Stripe Connect Completati', value: formatNumber(activation.stripeConnect?.completed) },
                        { label: 'Stripe Completion Rate', value: `${activation.stripeConnect?.completionRate}%` }
                    ]
                },
                {
                    title: 'OPERATIONS',
                    rows: [
                        { label: 'Device Offline', value: formatNumber(operations.devices?.offline) },
                        { label: 'Device Segnale Assente >24h', value: formatNumber(operations.devices?.staleSignalCount) },
                        { label: 'Pagamenti Falliti (periodo)', value: formatNumber(operations.payments?.failed) },
                        { label: 'Failure Rate', value: `${operations.payments?.failureRate}%` },
                        { label: 'Transfer Reversati (periodo)', value: formatNumber(operations.payments?.reversedTransfers) },
                        { label: 'Worker Stripe Incompleto', value: formatNumber(operations.workerIssues?.stripeOnboardingIncomplete) },
                        { label: 'Invoice Run con Errori (periodo)', value: formatNumber(operations.invoiceRuns?.issueCount) }
                    ]
                }
            ];

            const csv = buildCsvContent(sections);
            const filename = `soontip-analytics-${filters.from}-${filters.to}.csv`;
            downloadCsv(csv, filename);

            setStatus({ type: 'success', message: `Export completato: ${filename}` });
        } catch (err) {
            console.error('Export failed:', err);
            setStatus({ type: 'error', message: err.response?.data?.message || err.message || 'Export fallito' });
        } finally {
            setLoading(false);
        }
    }, [filters]);

    return (
        <AnalyticsLayout filters={filters} onFiltersChange={setFilters}>
            <PageGuide
                title="Export metriche per investor update e fundraising"
                summary="Genera un CSV completo con tutti i KPI del periodo. Prima di esportare, completa la checklist per garantire l'accuratezza dei dati."
            />
            <div style={{ maxWidth: '600px' }}>
                {/* Pre-export checklist */}
                <div style={{
                    background: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderLeft: '4px solid #22c55e',
                    borderRadius: '8px',
                    padding: '14px 18px',
                    marginBottom: '20px',
                    fontSize: '0.84rem',
                    color: '#14532d'
                }}>
                    <div style={{ fontWeight: 700, marginBottom: '8px' }}>✅ Checklist pre-export</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'default' }}>
                            <input type="checkbox" defaultChecked={false} />
                            Il periodo selezionato è corretto (<strong>{filters.from} → {filters.to}</strong>)
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'default' }}>
                            <input type="checkbox" defaultChecked={false} />
                            I valori di revenue sono chiaramente distinti tra <em>stimati</em> e <em>fatturati</em>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'default' }}>
                            <input type="checkbox" defaultChecked={false} />
                            I dati di test sono stati esclusi o identificati, se applicabile
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'default' }}>
                            <input type="checkbox" defaultChecked={false} />
                            I problemi operativi attivi sono stati esaminati (tab Operazioni)
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'default' }}>
                            <input type="checkbox" defaultChecked={false} />
                            Le definizioni delle metriche principali sono incluse nel contesto del report
                        </label>
                    </div>
                </div>

                <div style={{
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    padding: '28px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                        <Download size={20} color="#6366f1" />
                        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
                            Investor / Operations Export
                        </h2>
                    </div>

                    <p style={{ margin: '0 0 16px', fontSize: '0.875rem', color: '#64748b', lineHeight: 1.5 }}>
                        Esporta tutti i KPI del periodo selezionato in un unico file CSV.
                        Comprende: Overview, Traction, Revenue, Activation, Operations.
                    </p>

                    <ul style={{ margin: '0 0 20px', paddingLeft: '20px', fontSize: '0.85rem', color: '#475569', lineHeight: 1.8 }}>
                        <li>Periodo: <strong>{filters.from} → {filters.to}</strong></li>
                        <li>Formato: CSV UTF-8 (compatibile con Excel, Google Sheets)</li>
                        <li>Le metriche "all-time" ignorano il filtro data</li>
                        <li>Fee stimata: da snapshot al momento del pagamento</li>
                        <li>Revenue confermata: da fatture emesse nel periodo</li>
                    </ul>

                    {status && (
                        <div className={`alert alert-${status.type === 'success' ? 'success' : 'error'}`} style={{ marginBottom: '16px' }}>
                            {status.message}
                        </div>
                    )}

                    <button
                        onClick={handleExport}
                        disabled={loading}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 20px',
                            background: loading ? '#e2e8f0' : '#6366f1',
                            color: loading ? '#64748b' : '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'background 0.15s'
                        }}
                    >
                        <Download size={16} />
                        {loading ? 'Generazione in corso...' : 'Scarica CSV Completo'}
                    </button>
                </div>
            </div>
        </AnalyticsLayout>
    );
}
